/* eslint-disable jsx-a11y/media-has-caption */
/* eslint-disable react/jsx-no-bind */
/* eslint-disable no-nested-ternary */
import React, { useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import { IoMdAttach } from "react-icons/io";
import {
  FiSend,
  FiMic,
  FiSquare,
  FiArrowLeft,
  FiSmile,
  FiFileText,
} from "react-icons/fi";
import EmojiPicker, { EmojiClickData } from "emoji-picker-react";
import {
  useWhatsAppConversas,
  useEnviarMensagem,
  useEnviarArquivo,
  useEditarMensagem,
  useExcluirMensagem,
  useMarcarLido,
} from "services/api/AppWeb_WhatsApp/useWhatsAppConversas";
import { WhatsAppLead, WhatsAppMsg } from "services/api/AppWeb_WhatsApp/types";
import useAutoResizeTextarea from "hooks/useAutoResizeTextarea";
import { MsgList } from "./MsgBubble";
import { avatarColor, iniciais } from "../utils";
import * as S from "../styles";

interface PainelChatProps {
  lead: WhatsAppLead;
  onBack?: () => void;
  onSent?: (payload: {
    leadId: string;
    leadNome: string;
    mensagem: string;
    status?: "ENVIADA" | "ENTREGUE" | "LIDA";
  }) => void;
}

type AttachmentKind = "image" | "video" | "audio" | "pdf" | "document";

interface PendingAttachment {
  file: File;
  kind: AttachmentKind;
  previewUrl?: string;
}

function getAttachmentKind(file: File): AttachmentKind {
  if (file.type.startsWith("image/")) return "image";
  if (file.type.startsWith("video/")) return "video";
  if (file.type.startsWith("audio/")) return "audio";
  if (file.type === "application/pdf") return "pdf";
  return "document";
}

function formatFileSize(size: number): string {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

const PainelChat: React.FC<PainelChatProps> = ({ lead, onBack, onSent }) => {
  const {
    data: msgs = [],
    dataUpdatedAt,
    isLoading: loadingMsgs,
  } = useWhatsAppConversas(lead.id);
  const enviar = useEnviarMensagem();
  const enviarArquivo = useEnviarArquivo();
  const editarMensagem = useEditarMensagem();
  const excluirMensagem = useExcluirMensagem();
  const marcarLido = useMarcarLido();

  useEffect(() => {
    marcarLido(lead.id);
  }, [lead.id]);

  const [texto, setTexto] = useState("");
  const bottom = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const prevMsgIds = useRef<Set<string>>(new Set());
  const [newMsgCount, setNewMsgCount] = useState(0);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [editingMsgId, setEditingMsgId] = useState<string | null>(null);
  const [editingMsgPreview, setEditingMsgPreview] = useState("");
  const [pendingAttachment, setPendingAttachment] =
    useState<PendingAttachment | null>(null);
  const [recording, setRecording] = useState(false);
  const [recSecs, setRecSecs] = useState(0);
  const recRef = useRef<MediaRecorder | null>(null);
  const recChunks = useRef<Blob[]>([]);
  const recTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  useAutoResizeTextarea(inputRef, texto, { maxLines: 7, enabled: !recording });

  useEffect(() => {
    if (msgs.length === 0) return;
    const incoming = msgs.filter(
      (m: WhatsAppMsg) =>
        m.direcao === "entrada" && !prevMsgIds.current.has(m.id)
    );
    if (incoming.length > 0 && prevMsgIds.current.size > 0)
      setNewMsgCount((c) => c + incoming.length);
    msgs.forEach((m: WhatsAppMsg) => prevMsgIds.current.add(m.id));
  }, [dataUpdatedAt]);

  useEffect(() => {
    bottom.current?.scrollIntoView({ behavior: "smooth" });
    setNewMsgCount(0);
  }, [msgs]);

  useEffect(() => {
    if (!emojiOpen) return undefined;

    function handleClickOutside(e: MouseEvent) {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(e.target as Node)
      ) {
        setEmojiOpen(false);
      }
    }

    function handleEsc(e: KeyboardEvent) {
      if (e.key === "Escape") setEmojiOpen(false);
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEsc);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [emojiOpen]);

  useEffect(() => {
    if (recording) setEmojiOpen(false);
  }, [recording]);

  useEffect(
    () => () => {
      if (pendingAttachment?.previewUrl) {
        URL.revokeObjectURL(pendingAttachment.previewUrl);
      }
    },
    [pendingAttachment]
  );

  function clearPendingAttachment() {
    setPendingAttachment((prev) => {
      if (prev?.previewUrl) URL.revokeObjectURL(prev.previewUrl);
      return null;
    });
  }

  function setPendingAttachmentFromFile(file: File) {
    const kind = getAttachmentKind(file);
    const previewUrl =
      kind === "image" || kind === "video" || kind === "audio"
        ? URL.createObjectURL(file)
        : undefined;

    setPendingAttachment((prev) => {
      if (prev?.previewUrl) URL.revokeObjectURL(prev.previewUrl);
      return { file, kind, previewUrl };
    });
  }

  function handleEmojiSelect(emojiData: EmojiClickData) {
    if (recording) return;
    const input = inputRef.current;
    const { emoji } = emojiData;

    if (!input) {
      setTexto((prev) => `${prev}${emoji}`);
      return;
    }

    const start = input.selectionStart ?? texto.length;
    const end = input.selectionEnd ?? texto.length;
    const nextText = `${texto.slice(0, start)}${emoji}${texto.slice(end)}`;

    setTexto(nextText);
    requestAnimationFrame(() => {
      input.focus();
      const pos = start + emoji.length;
      input.setSelectionRange(pos, pos);
    });
  }

  async function send(e: React.FormEvent) {
    e.preventDefault();
    if (!texto.trim()) return;
    const t = texto;

    if (editingMsgId) {
      try {
        await editarMensagem.mutateAsync({
          leadId: lead.id,
          idMensagem: editingMsgId,
          mensagem: t,
        });
        setTexto("");
        setEditingMsgId(null);
        setEditingMsgPreview("");
      } catch {
        // Erro já tratado no hook
      }
      return;
    }

    setTexto("");
    try {
      await enviar.mutateAsync({ leadId: lead.id, mensagem: t });
      onSent?.({
        leadId: lead.id,
        leadNome: lead.nome,
        mensagem: t,
        status: "ENVIADA",
      });
    } catch {
      // Erro já tratado pelo onError do hook
    }
  }

  function handleDeleteMessage(msg: WhatsAppMsg) {
    if (msg.direcao !== "saida") return;
    if (msg.id.startsWith("tmp-")) {
      toast.warn("Aguarde o envio antes de excluir a mensagem.");
      return;
    }

    excluirMensagem.mutate(
      { leadId: lead.id, idMensagem: msg.id },
      {
        onSuccess: () => {
          if (editingMsgId === msg.id) {
            setEditingMsgId(null);
            setTexto("");
            setEditingMsgPreview("");
          }
        },
      }
    );
  }

  function handleEditMessage(msg: WhatsAppMsg) {
    if (msg.direcao !== "saida") return;
    if (msg.id.startsWith("tmp-")) {
      toast.warn("Aguarde o envio antes de editar a mensagem.");
      return;
    }
    if (msg.mediaUrl || msg.mediaType || !msg.mensagem?.trim()) return;

    clearPendingAttachment();
    setEditingMsgId(msg.id);
    setEditingMsgPreview(msg.mensagem || "");
    setTexto(msg.mensagem);
    setEmojiOpen(false);
    requestAnimationFrame(() => inputRef.current?.focus());
  }

  function cancelEditingMessage() {
    setEditingMsgId(null);
    setEditingMsgPreview("");
    setTexto("");
  }

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setPendingAttachmentFromFile(file);
    setEditingMsgId(null);
    setEditingMsgPreview("");
    setTexto("");
  }

  function handlePaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    if (recording || sending) return;
    const fileItem = Array.from(e.clipboardData.items).find(
      (item) => item.kind === "file"
    );
    const file = fileItem?.getAsFile();
    if (!file) return;

    e.preventDefault();
    setPendingAttachmentFromFile(file);
    setEmojiOpen(false);
  }

  async function sendPendingAttachment() {
    if (!pendingAttachment) return;
    try {
      setEditingMsgId(null);
      setEditingMsgPreview("");
      setTexto("");
      await enviarArquivo.mutateAsync({
        leadId: lead.id,
        file: pendingAttachment.file,
      });
      onSent?.({
        leadId: lead.id,
        leadNome: lead.nome,
        mensagem: pendingAttachment.file.name,
        status: "ENVIADA",
      });
      clearPendingAttachment();
    } catch {
      toast.error("Erro ao enviar arquivo");
    }
  }

  async function toggleRecording() {
    if (recording) {
      recRef.current?.stop();
      if (recTimer.current) clearInterval(recTimer.current);
      setRecording(false);
      setRecSecs(0);
    } else {
      try {
        setEditingMsgId(null);
        setEditingMsgPreview("");
        setTexto("");
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        const mr = new MediaRecorder(stream);
        recRef.current = mr;
        recChunks.current = [];
        mr.ondataavailable = (ev) => {
          if (ev.data.size > 0) recChunks.current.push(ev.data);
        };
        mr.onstop = async () => {
          stream.getTracks().forEach((t) => t.stop());
          const blob = new Blob(recChunks.current, { type: "audio/webm" });
          const file = new File([blob], "audio.webm", { type: "audio/webm" });
          try {
            await enviarArquivo.mutateAsync({ leadId: lead.id, file });
            onSent?.({
              leadId: lead.id,
              leadNome: lead.nome,
              mensagem: "Áudio",
              status: "ENVIADA",
            });
          } catch {
            toast.error("Erro ao enviar áudio");
          }
        };
        mr.start();
        setRecording(true);
        setRecSecs(0);
        recTimer.current = setInterval(() => setRecSecs((s) => s + 1), 1000);
      } catch {
        toast.error("Sem permissão para microfone");
      }
    }
  }

  const sending =
    enviar.isLoading ||
    enviarArquivo.isLoading ||
    editarMensagem.isLoading ||
    excluirMensagem.isLoading;

  return (
    <S.ChatPanel>
      <S.ChatHeader>
        {onBack && (
          <S.BackButton type="button" onClick={onBack}>
            <FiArrowLeft size={20} />
          </S.BackButton>
        )}
        <S.Avatar bgColor={avatarColor(lead.id)} size={40}>
          {iniciais(lead.nome)}
          {lead.fotoUrl && (
            <S.AvatarImg
              src={lead.fotoUrl}
              alt=""
              referrerPolicy="no-referrer"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
          )}
        </S.Avatar>
        <S.ChatHeaderInfo>
          <S.ChatHeaderName>{lead.nome}</S.ChatHeaderName>
          <S.ChatHeaderSub>
            <S.OnlineDot />
            {lead.telefone} · {lead.empreendimento}
          </S.ChatHeaderSub>
        </S.ChatHeaderInfo>
      </S.ChatHeader>

      <S.MessagesArea>
        {newMsgCount > 0 && (
          <S.NewMsgBanner
            type="button"
            onClick={() => {
              bottom.current?.scrollIntoView({ behavior: "smooth" });
              setNewMsgCount(0);
            }}
          >
            {newMsgCount} nova{newMsgCount > 1 ? "s" : ""} mensagem
            {newMsgCount > 1 ? "s" : ""} ↓
          </S.NewMsgBanner>
        )}

        {loadingMsgs ? (
          <S.ChatCenterNotice>
            <S.ChatNoticeBox>
              <S.TypingDots>
                <S.Dot />
                <S.Dot />
                <S.Dot />
              </S.TypingDots>
              <S.ChatNoticeText>Carregando mensagens</S.ChatNoticeText>
            </S.ChatNoticeBox>
          </S.ChatCenterNotice>
        ) : msgs.length === 0 ? (
          <S.ChatCenterNotice>
            <S.ChatNoticeBox>
              <S.ChatNoticeText>
                Nenhuma mensagem nesta conversa.
              </S.ChatNoticeText>
              <S.ChatNoticeHint>
                Envie uma mensagem para começar.
              </S.ChatNoticeHint>
            </S.ChatNoticeBox>
          </S.ChatCenterNotice>
        ) : (
          <MsgList
            msgs={msgs}
            onEditMessage={handleEditMessage}
            onDeleteMessage={handleDeleteMessage}
            editingMessageId={editingMsgId}
          />
        )}
        <div ref={bottom} />
      </S.MessagesArea>

      {editingMsgId && (
        <S.EditingMessageBar>
          <S.EditingMessageText>
            Editando: {editingMsgPreview || "mensagem"}
          </S.EditingMessageText>
          <S.EditingMessageCancel type="button" onClick={cancelEditingMessage}>
            Cancelar
          </S.EditingMessageCancel>
        </S.EditingMessageBar>
      )}

      {pendingAttachment && (
        <S.AttachmentPreviewBar>
          <S.AttachmentPreviewMedia>
            {pendingAttachment.kind === "image" &&
              pendingAttachment.previewUrl && (
                <img
                  src={pendingAttachment.previewUrl}
                  alt={pendingAttachment.file.name}
                />
              )}
            {pendingAttachment.kind === "video" &&
              pendingAttachment.previewUrl && (
                <video src={pendingAttachment.previewUrl} controls />
              )}
            {pendingAttachment.kind === "audio" &&
              pendingAttachment.previewUrl && (
                <audio src={pendingAttachment.previewUrl} controls />
              )}
            {(pendingAttachment.kind === "pdf" ||
              pendingAttachment.kind === "document") && (
              <S.AttachmentDocIcon>
                <FiFileText size={18} />
              </S.AttachmentDocIcon>
            )}
          </S.AttachmentPreviewMedia>
          <S.AttachmentPreviewInfo>
            <S.AttachmentPreviewName>
              {pendingAttachment.file.name}
            </S.AttachmentPreviewName>
            <S.AttachmentPreviewMeta>
              {pendingAttachment.kind.toUpperCase()} ·{" "}
              {formatFileSize(pendingAttachment.file.size)}
            </S.AttachmentPreviewMeta>
          </S.AttachmentPreviewInfo>
          <S.AttachmentPreviewActions>
            <S.AttachmentActionButton
              type="button"
              onClick={clearPendingAttachment}
              disabled={sending}
            >
              Cancelar
            </S.AttachmentActionButton>
            <S.AttachmentActionButton
              type="button"
              primary
              onClick={sendPendingAttachment}
              disabled={sending}
            >
              {sending ? "Enviando..." : "Enviar"}
            </S.AttachmentActionButton>
          </S.AttachmentPreviewActions>
        </S.AttachmentPreviewBar>
      )}

      <S.ChatInputArea onSubmit={send}>
        <input
          ref={fileRef}
          type="file"
          style={{ display: "none" }}
          onChange={handleFile}
          accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx"
        />
        <S.ChatInputActions>
          <S.IconBtn
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={sending}
            aria-label="Anexar arquivo"
          >
            <IoMdAttach size={20} />
          </S.IconBtn>
          <S.EmojiPickerWrapper ref={emojiPickerRef}>
            <S.IconBtn
              type="button"
              onClick={() => setEmojiOpen((v) => !v)}
              disabled={sending || recording}
              aria-label="Abrir seletor de emoji"
              aria-expanded={emojiOpen}
            >
              <FiSmile size={18} />
            </S.IconBtn>

            {emojiOpen && (
              <S.EmojiPopover>
                <EmojiPicker
                  onEmojiClick={handleEmojiSelect}
                  width="100%"
                  height={360}
                  lazyLoadEmojis
                  searchDisabled={false}
                  skinTonesDisabled={false}
                  previewConfig={{ showPreview: false }}
                />
              </S.EmojiPopover>
            )}
          </S.EmojiPickerWrapper>
        </S.ChatInputActions>

        {recording ? (
          <S.RecordingBar>
            <S.RecordDot />
            <S.RecordTime>
              {Math.floor(recSecs / 60)
                .toString()
                .padStart(2, "0")}
              :{(recSecs % 60).toString().padStart(2, "0")}
            </S.RecordTime>
            <S.RecordLabel>Gravando...</S.RecordLabel>
          </S.RecordingBar>
        ) : (
          <S.ChatInput
            ref={inputRef}
            rows={1}
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            onPaste={handlePaste}
            placeholder={
              editingMsgId ? "Edite sua mensagem..." : "Digite uma mensagem..."
            }
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                send(e as any);
              }
            }}
          />
        )}

        {texto.trim() ? (
          <S.SendButton type="submit" disabled={sending}>
            <FiSend size={18} />
          </S.SendButton>
        ) : (
          <S.SendButton
            type="button"
            onClick={toggleRecording}
            disabled={sending}
            variant={recording ? "danger" : undefined}
          >
            {recording ? <FiSquare size={16} /> : <FiMic size={18} />}
          </S.SendButton>
        )}
      </S.ChatInputArea>
    </S.ChatPanel>
  );
};

export default PainelChat;
